import { FC, useMemo } from 'react'
import { Button, Flex, Table } from 'antd'
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined'
import { skipToken } from '@reduxjs/toolkit/query'
import '../../../orders.css'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'
import { useGetOrderRequestQuery } from '../../../../../store/api/order-requests-api'
import useEndpointQueryParams from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import OrderRequest from '../../../../../types/api/order-request'
import FormModal from '../../../../../components/form-modal/form-modal'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import FiltersForm from './components/filters-form/filters-form'
import { columns } from './constants/columns'
import CreateOrderRequestForm from './components/create-order-request-form/create-order-request-form'
import { deserializeFilters } from './helpers/deserialize-filters'
import { useUser } from '../../../../../hooks/use-user'

const MaterialOrderRequests: FC = () => {
    const user = useUser()
    const params = useEndpointQueryParams({ deserializeFilters })

    const orderRequestsParams: EndpointQueryParameters | typeof skipToken = useMemo(() => {
        if (!user?.documentId) {
            return skipToken
        }

        return {
            populate: {
                initiator: true,
                unit: true,
                material: {
                    populate: {
                        material_type: true,
                    },
                },
                shipment: {
                    populate: {
                        client: true,
                    },
                },
                receipt_position: {
                    populate: {
                        receipt: true,
                    },
                },
            },
            pagination: params.pagination,
            filters: {
                ...params.filters,
                initiator: { documentId: { $eq: user.documentId } },
            },
            sort: ['createdAt:desc'],
        }
    }, [params.filters, params.pagination, user?.documentId])

    const { data, isLoading, refetch } = useGetOrderRequestQuery(orderRequestsParams)
    const pagination = useTablePagination(params, data)
    const dataSource: OrderRequest[] = useMemo(() => data?.data || [], [data])

    const rowClassName = (record: OrderRequest): string => {
        switch (record.requestStatus) {
            case 'Обработан':
                return 'processed-row'
            case 'Отменен':
                return 'disabled-row'

            default:
                return ''
        }
    }

    return (
        <>
            <Flex
                style={{ flex: '0 0 auto', marginBottom: '16px' }}
                justify={'space-between'}
                align={'flex-start'}
                gap={16}
                wrap
            >
                <FiltersForm />
                <FormModal
                    title={'Создание заявки'}
                    width={900}
                    renderOpener={onOpen => (
                        <Button
                            type={'primary'}
                            size={'middle'}
                            icon={<PlusOutlined />}
                            onClick={onOpen}
                        >
                            Создать заявку
                        </Button>
                    )}
                    renderContent={onClose => (
                        <CreateOrderRequestForm
                            onSuccess={() => {
                                refetch()
                                onClose()
                            }}
                        />
                    )}
                />
            </Flex>
            <TableWrapper style={{ flex: 'auto' }} currentTableHeight={75}>
                <Table
                    bordered
                    columns={columns}
                    rowClassName={rowClassName}
                    dataSource={dataSource}
                    pagination={pagination}
                    loading={isLoading}
                    rowKey={'documentId'}
                />
            </TableWrapper>
        </>
    )
}

export default MaterialOrderRequests
